import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import materiaService from '../services/MateriaService';
import cursoService from '../services/CursoService';
import aulaService from '../services/AulaService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import LoadingSpinner from './common/LoadingSpinner';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import SearchableSelect from './common/SearchableSelect';

const hasModuloAlumnosEnabled = (flags) =>
  isFeatureEnabled(flags, 'ModuloAlumnos');

const hasAgentesClasicosEnabled = (flags) =>
  isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

const normalizeListResponse = (response) => (
  Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : []
);

const normalizeIdList = (value) => (
  Array.isArray(value)
    ? value.map((item) => (item == null ? '' : String(item))).filter(Boolean)
    : []
);

const formatCourseLabel = (curso) => (
  curso?.nombre
  || curso?.Nombre
  || `${curso?.valorAnio || curso?.ValorAnio || '—'}° ${curso?.division || curso?.Division || '—'} - ${curso?.cicloLectivo || curso?.CicloLectivo || '—'}`
);

const ProfesorEditAula = () => {
  const navigate = useNavigate();
  const { aulaId } = useParams();
  const { user, logout } = useAuth();

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => hasModuloAlumnosEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);
  const canCreateAula = Boolean(user?.permiteCrearAula);

  const [materias, setMaterias] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [classicAssistants, setClassicAssistants] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    nicknameUrl: '',
    codigoSuscripcion: '',
    aulaPublica: false,
    idMateria: '',
    idCurso: '',
    idsAsistentes: [],
    cicloLectivo: currentYear,
    idColegio: activeColegioId || ''
  });

  useEffect(() => {
    if (!aulaId || !hasModuloAlumnos || !activeColegioId) return;

    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const requests = [
          materiaService.getMisMaterias({ idColegio: activeColegioId }),
          cursoService.getMisCursosProfesor({ idColegio: activeColegioId }),
          aulaService.getMiAulaById(aulaId)
        ];

        if (hasAgentesClasicos) {
          requests.push(aulaService.getMisAsistentesGptClasicosParaAula({ idColegio: activeColegioId }));
        }

        const [materiasRes, cursosRes, aulaRes, assistantsRes = []] = await Promise.all(requests);
        setMaterias(normalizeListResponse(materiasRes));
        setCursos(normalizeListResponse(cursosRes));
        setClassicAssistants(Array.isArray(assistantsRes) ? assistantsRes : []);

        if (aulaRes) {
          const selectedAssistants = normalizeIdList(aulaRes.idsAsistentes || aulaRes.IdsAsistentes);
          setForm({
            nombre: aulaRes.nombre || aulaRes.Nombre || '',
            descripcion: aulaRes.descripcion || aulaRes.Descripcion || '',
            nicknameUrl: aulaRes.nicknameUrl || aulaRes.NicknameUrl || '',
            codigoSuscripcion: aulaRes.codigoSuscripcion || aulaRes.CodigoSuscripcion || '',
            aulaPublica: !!(aulaRes.aulaPublica ?? aulaRes.AulaPublica),
            idMateria: String(aulaRes.idMateria || aulaRes.IdMateria || ''),
            idCurso: String(aulaRes.idCurso || aulaRes.IdCurso || ''),
            idsAsistentes: selectedAssistants,
            cicloLectivo: aulaRes.cicloLectivo || aulaRes.CicloLectivo || currentYear,
            idColegio: aulaRes.idColegio || aulaRes.IdColegio || activeColegioId || ''
          });
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el aula.');
        setMessageType('error');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [aulaId, activeColegioId, currentYear, hasAgentesClasicos, hasModuloAlumnos]);

  const selectedCycle = Number(form.cicloLectivo) || currentYear;

  const availableCourses = useMemo(() => (
    cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === selectedCycle)
      .sort((a, b) => formatCourseLabel(a).localeCompare(formatCourseLabel(b), 'es', { sensitivity: 'base' }))
  ), [cursos, selectedCycle]);

  const cursoOptions = useMemo(() => (
    availableCourses
      .map((curso) => {
        const id = String(curso?.id || curso?.Id || '');
        const nombre = formatCourseLabel(curso);
        const cicloLectivo = curso?.cicloLectivo || curso?.CicloLectivo || '';
        const valorAnio = curso?.valorAnio || curso?.ValorAnio || '';
        const division = curso?.division || curso?.Division || '';
        const secondary = [valorAnio ? `${valorAnio}°` : '', division ? `División ${division}` : '', cicloLectivo ? `Ciclo ${cicloLectivo}` : '']
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombre,
          secondary,
          searchText: `${nombre} ${valorAnio} ${division} ${cicloLectivo}`.trim()
        };
      })
      .filter((option) => option.value)
  ), [availableCourses]);

  const materiaOptions = useMemo(() => (
    materias
      .map((materia) => {
        const id = String(materia?.id || materia?.Id || '');
        const nombre = materia?.nombre || materia?.Nombre || 'Materia';
        const codigo = materia?.codigo || materia?.Codigo || '';
        const area = materia?.nombreArea || materia?.NombreArea || '';
        const label = codigo ? `${nombre} · ${codigo}` : nombre;

        return {
          value: id,
          label,
          secondary: area,
          searchText: `${nombre} ${codigo} ${area}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [materias]);

  const assistantOptions = useMemo(() => (
    classicAssistants
      .map((assistant) => {
        const id = String(assistant?.idOpenAIAssistant || assistant?.IdOpenAIAssistant || '');
        const nombreVisible = assistant?.nombreVisible || assistant?.NombreVisible || assistant?.nombre || assistant?.Nombre || 'Asistente IA';
        const nombreMateria = assistant?.nombreMateria || assistant?.NombreMateria || '';
        const nombreArea = assistant?.nombreArea || assistant?.NombreArea || '';
        const esAsignado = !!(assistant?.esAsignado ?? assistant?.EsAsignado);
        const secondary = [nombreMateria, nombreArea, esAsignado ? 'Asignado directo' : 'Por materia']
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombreVisible,
          secondary,
          searchText: `${nombreVisible} ${nombreMateria} ${nombreArea}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [classicAssistants]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCycleChange = (value) => {
    const nextCycle = Number(value) || currentYear;
    const nextAvailableIds = cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === nextCycle)
      .map((curso) => String(curso?.id || curso?.Id || ''))
      .filter(Boolean);
    const nextAvailableSet = new Set(nextAvailableIds);

    setForm((prev) => ({
      ...prev,
      cicloLectivo: value,
      idCurso: nextAvailableSet.has(String(prev.idCurso)) ? prev.idCurso : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateAula) {
      setMessage('No tienes permisos para editar aulas.');
      setMessageType('error');
      return;
    }
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del aula.');
      setMessageType('error');
      return;
    }
    if (!form.idMateria) {
      setMessage('Selecciona la materia.');
      setMessageType('error');
      return;
    }
    if (!form.nicknameUrl.trim()) {
      setMessage('Completa el nickname del aula.');
      setMessageType('error');
      return;
    }
    if (!form.idCurso) {
      setMessage('Selecciona el curso al que pertenecerá el aula.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await aulaService.updateAulaProfesor(aulaId, {
        nombre: form.nombre,
        descripcion: form.descripcion,
        nicknameUrl: form.nicknameUrl,
        codigoSuscripcion: '',
        aulaPublica: form.aulaPublica,
        idMateria: form.idMateria,
        idCurso: form.idCurso,
        idsAsistentes: form.idsAsistentes,
        cicloLectivo: Number(form.cicloLectivo) || currentYear,
        idColegio: form.idColegio
      });
      setMessage('Aula actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/my-aulas', { replace: true }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el aula.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasModuloAlumnos) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Aulas</h1>
            <p className="dashboard-subtitle">El módulo de alumnos aún no está habilitado para este colegio.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dashboard">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        <ProfessorMenu
          activeId="aulas"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={(item) => item?.path && navigate(item.path)}
        />
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar aula</h1>
            <p className="page-subtitle">Actualiza el aula de {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <button className="logout-btn-header" onClick={logout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-door-open" />
                <span>Datos del aula</span>
              </div>
            </div>
            <div className="card-content">
              {!canCreateAula && (
                <p style={{ marginTop: 0, color: '#b91c1c' }}>
                  No tienes permisos para editar aulas. Contacta al administrador.
                </p>
              )}
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field">
                  <label>Nickname URL</label>
                  <input
                    type="text"
                    value={form.nicknameUrl}
                    onChange={(e) => handleChange('nicknameUrl', e.target.value)}
                    placeholder="Ej: aula-bio-2026"
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.aulaPublica}
                      onChange={(e) => handleChange('aulaPublica', e.target.checked)}
                      disabled={!canCreateAula}
                    />
                    Aula pública
                  </label>
                </div>
                <div className="form-field">
                  <label>Ciclo lectivo</label>
                  <input
                    type="number"
                    value={form.cicloLectivo}
                    onChange={(e) => handleCycleChange(e.target.value)}
                    min="2000"
                    max="2100"
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field">
                  <label>Curso del aula</label>
                  <SearchableSelect
                    value={form.idCurso}
                    onChange={(value) => handleChange('idCurso', value)}
                    options={cursoOptions}
                    placeholder="Busca un curso por nombre, año, división o ciclo"
                    emptyText={`No hay cursos asignados para el ciclo lectivo ${selectedCycle}.`}
                    noResultsText="No encontramos cursos con esa búsqueda."
                    disabled={isLoadingData || !canCreateAula}
                  />
                  <small style={{ color: '#64748b' }}>
                    Solo puedes elegir cursos asignados al profesor dentro del ciclo lectivo {selectedCycle}.
                  </small>
                </div>
                <div className="form-field">
                  <label>Materia</label>
                  <SearchableSelect
                    value={form.idMateria}
                    onChange={(value) => handleChange('idMateria', value)}
                    options={materiaOptions}
                    placeholder="Busca una materia por nombre o código"
                    emptyText="No hay materias asignadas disponibles."
                    noResultsText="No encontramos materias con esa búsqueda."
                    disabled={isLoadingData || !canCreateAula}
                  />
                </div>
                {hasAgentesClasicos && (
                  <div className="form-field">
                    <label>Asistentes IA del aula</label>
                    <SearchableSelect
                      value={form.idsAsistentes}
                      onChange={(value) => handleChange('idsAsistentes', value)}
                      options={assistantOptions}
                      placeholder="Busca asistentes IA por nombre, materia o área"
                      emptyText="No tienes asistentes IA finalizados disponibles."
                      noResultsText="No encontramos asistentes IA con esa búsqueda."
                      disabled={isLoadingData || !canCreateAula}
                      multiple
                    />
                    <small style={{ color: '#64748b' }}>
                      El aula mostrará únicamente los asistentes IA seleccionados aquí.
                    </small>
                  </div>
                )}

                {message && (
                  <div className={`form-message ${messageType}`} style={{ marginBottom: '1rem' }}>
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => navigate('/my-aulas')}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={isSubmitting || !canCreateAula}>
                    {isSubmitting ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {(isLoadingData || isSubmitting) && (
        <LoadingSpinner message={isSubmitting ? 'Guardando aula...' : 'Obteniendo datos...'} />
      )}
    </div>
  );
};

export default ProfesorEditAula;
